import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Car, BookOpen, ClipboardCheck, RefreshCw, ArrowRight } from "lucide-react";

const OfferSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const offers = [
    {
      icon: Car,
      title: "Kurs Prawa Jazdy Kat. B",
      description:
        "Pełny kurs na prawo jazdy kategorii B. Szkolenie teoretyczne i praktyczne dostosowane do Twoich potrzeb.",
      features: ["30 godzin teorii", "30 godzin praktyki", "Materiały szkoleniowe"],
      highlight: false,
    },
    {
      icon: BookOpen,
      title: "Zajęcia Teoretyczne",
      description:
        "Kompleksowe przygotowanie teoretyczne z wykorzystaniem nowoczesnych materiałów dydaktycznych.",
      features: ["Wykłady stacjonarne", "Testy próbne", "Dostęp do e-learningu"],
    },
    {
      icon: ClipboardCheck,
      title: "Egzamin Wewnętrzny",
      description:
        "Sprawdzian umiejętności przed przystąpieniem do egzaminu państwowego w MORD Nowy Targ.",
      features: ["Test teoretyczny", "Jazda próbna", "Omówienie błędów"],
    },
    {
      icon: RefreshCw,
      title: "Jazdy Doszkalające",
      description:
        "Dodatkowe godziny jazdy dla osób potrzebujących jazd doszkalających.",
      features: ["Elastyczne terminy", "Jazda po trasach egzaminacyjnych", "Korekta błędów"],
    },
  ];

  const scrollToContact = () => {
    const element = document.querySelector("#kontakt");
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="oferta"
      className="py-20 lg:py-32 bg-slate-50"
      data-testid="offer-section"
      ref={ref}
    >
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="inline-block bg-yellow-400/20 text-slate-900 px-4 py-2 rounded-sm font-heading font-bold text-sm tracking-wider mb-6">
            OFERTA
          </div>
          <h2
            className="font-heading font-bold text-4xl lg:text-5xl text-slate-900 tracking-tight mb-6"
            data-testid="offer-title"
          >
            NASZA OFERTA SZKOLENIOWA
          </h2>
          <p className="text-slate-600 text-lg">
            Wybierz szkolenie dopasowane do Twoich potrzeb. Oferujemy pełne kursy oraz jazdy doszkalające.
          </p>
        </motion.div>

        {/* Offers Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="offer-grid">
          {offers.map((offer, index) => (
            <motion.div
              key={offer.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`feature-card group cursor-pointer ${
                offer.highlight ? "border-yellow-400 bg-slate-900" : ""
              }`}
              onClick={scrollToContact}
              data-testid={`offer-card-${index}`}
            >
              <div
                className={`w-14 h-14 rounded-sm flex items-center justify-center mb-6 ${
                  offer.highlight ? "bg-yellow-400" : "bg-slate-900 group-hover:bg-yellow-400"
                } transition-colors`}
              >
                <offer.icon
                  className={`w-7 h-7 ${
                    offer.highlight ? "text-slate-900" : "text-yellow-400 group-hover:text-slate-900"
                  } transition-colors`}
                />
              </div>

              <h3
                className={`font-heading font-bold text-xl tracking-tight mb-3 ${
                  offer.highlight ? "text-white" : "text-slate-900"
                }`}
              >
                {offer.title}
              </h3>

              <p
                className={`mb-6 leading-relaxed ${
                  offer.highlight ? "text-slate-100 font-medium" : "text-slate-600"
                }`}
              >
                {offer.description}
              </p>

              <ul className="space-y-2 mb-6">
                {offer.features.map((feature, i) => (
                  <li
                    key={i}
                    className={`flex items-center gap-2 text-sm ${
                      offer.highlight ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div
                className={`flex items-center gap-2 font-heading font-bold text-sm tracking-wider ${
                  offer.highlight ? "text-yellow-400" : "text-slate-900"
                } group-hover:gap-4 transition-all`}
              >
                ZAPYTAJ O SZCZEGÓŁY
                <ArrowRight className="w-4 h-4" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-slate-600 mb-4">
            Masz pytania? Zadzwoń lub napisz do nas!
          </p>
          <button
            onClick={scrollToContact}
            className="btn-primary"
            data-testid="offer-cta"
          >
            Skontaktuj się z nami
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default OfferSection;
